import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete, Box, Grid, TextField, Typography } from '@mui/material'
import { addRegion, getRegions, type Region } from 'services'
import { useGlobalMessageHandler, useRegionLuts } from 'components'
import CircularProgress from '@mui/material/CircularProgress'
import { formatErrorFieldMessages } from 'interfaces'
import { AccessLevel, useSessionContext } from 'session'

interface RegionAutoCompleteProps {
  region: Region | null
  positionCountryId: number | null
  disabled: boolean
  setRegion: (region: Region | null) => void
  label: string
}

export const RegionAutoComplete: React.FC<RegionAutoCompleteProps> = ({ region, disabled, setRegion, positionCountryId, label }) => {
  const [selectedOption, setSelectedOption] = useState<Region | null>(region)
  const [inputValue, setInputValue] = useState<string>('')
  const { lutValues: regionLutValues, setLutValues } = useRegionLuts()
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()
  const [filteredOptions, setFilteredOptions] = useState<Region[]>([])
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingFromDatabase, setLoadingFromDatabase] = useState<boolean>(false)
  const [countryLocked, setCountryLocked] = useState<boolean>(true)
  const [unsafeCreation, setUnsafeCreation] = useState<boolean>(false)
  const { hasAccessLevel } = useSessionContext()

  const autocompleteCommands = [
    { inputValue: 'country_unlock', command: () => { setCountryLocked(false) }, message: 'Country lock disabled' },
    { inputValue: 'unsafe_creation', command: () => { setUnsafeCreation(true) }, message: 'Unsafe creation enabled. Be careful to not create duplicates' }
  ]

  useEffect(() => {
    if ((selectedOption?.desc == null || selectedOption?.desc === '') && selectedOption?.id != null && selectedOption?.id !== -1) {
      setSelectedOption(regionLutValues.regions.filter((region) => region.id === selectedOption.id)[0])
    }
    setRegion(selectedOption)
  }, [selectedOption?.id, selectedOption?.desc])

  useEffect(() => {
    if (region?.id !== selectedOption?.id) { setSelectedOption(region) }
  }, [region?.id, region?.desc])

  const removeAccents = (str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  const handleSpecialCommand = (input: string): boolean => {
    for (const cmd of autocompleteCommands) {
      if (`//${cmd.inputValue}` === input && hasAccessLevel(AccessLevel.MANAGER)) {
        cmd.command()
        showSuccessMessage(cmd.message)
        setFilteredOptions([])
        setInputValue('')
        return true
      }
    }
    return false
  }

  const onInputChange = (event: React.SyntheticEvent, newInputValue: string): void => {
    setInputValue(newInputValue)
    if (event?.type !== 'change' || newInputValue.length < 4 || handleSpecialCommand(newInputValue) || newInputValue.startsWith('//')) return
    setLoading(true)
    clearTimeout(timeoutRef.current)

    const timeoutId = setTimeout(() => {
      void fetchAndFilterRegions(newInputValue, timeoutId)
    }, 500)

    timeoutRef.current = timeoutId
  }
  const filterRegions = (input: string): Region[] => {
    return regionLutValues.regions.filter(({
      desc,
      countryId
    }) => removeAccents(desc.toLowerCase()).includes(input) &&
        (positionCountryId === countryId || !countryLocked)
    )
  }

  const fetchAndFilterRegions = async (inputValue: string, timeoutId: NodeJS.Timeout): Promise<void> => {
    const normalizedInput = removeAccents(inputValue.toLowerCase())
    let filtered = filterRegions(normalizedInput)
    if (filtered.length === 0) {
      try {
        setLoadingFromDatabase(true)
        const updatedRegions = await getRegions()
        setLutValues(updatedRegions)
        filtered = filterRegions(normalizedInput)
      } catch {
        showErrorMessage('Failed to update region list')
      } finally {
        setLoadingFromDatabase(false)
      }
    }
    const newRegionOption = { id: -1, desc: inputValue, countryId: positionCountryId }
    filtered = (unsafeCreation || filtered.length === 0)
      ? [newRegionOption, ...filtered]
      : filtered
    if (timeoutRef.current === timeoutId) {
      setFilteredOptions(filtered)
      setLoading(false)
    }
  }

  const handleOnChange = (option: Region | null): void => {
    if (option != null) {
      if (option.id === -1) {
        clearMessages()
        setLoading(true)
        addRegion(option).then((value) => {
          option.id = value.id
          setLutValues({ ...regionLutValues, regions: [...regionLutValues.regions, option] })
          setFilteredOptions([])
          setSelectedOption(option)
          showSuccessMessage('Region added')
          setUnsafeCreation(false)
        }).catch((error) => { showErrorMessage(formatErrorFieldMessages(error.response.data.errors)) })
          .finally(() => {
            setLoading(false)
          })
      }
    }
    setSelectedOption(option)
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={5}>
        <Autocomplete
          id="region-autocomplete"
          disabled={disabled}
          value={selectedOption}
          inputValue={inputValue}
          onInputChange={(event, newValue) => {
            onInputChange(event, newValue)
          }}
          onBlur={() => { setFilteredOptions([]) }}
          options={filteredOptions.length > 0 ? filteredOptions : regionLutValues.regions.filter((region) => region.countryId === positionCountryId || !countryLocked)}
          getOptionLabel={(option) => option.desc}
          isOptionEqualToValue={(option, value) =>
            option.id === value?.id
          }
          onChange={(event, value) => { handleOnChange(value) }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.id}>
              {option.desc}
              {option.id === -1 && <span style={{ marginLeft: '8px' }}>➕</span>}
            </Box>
          )}
        />
      </Grid>
      <Grid item xs={7}>
        {loading &&
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <CircularProgress color={loadingFromDatabase ? 'warning' : 'primary'}/>
            {loadingFromDatabase && <Typography sx={{ marginLeft: 1 }} variant={'caption'} >Updating Local LUT...</Typography>}
        </Box>
        }
      </Grid>
    </Grid>
  )
}
